/**
 * 22 Oct 2026 (D-1022-COMPANION): the other times volunteers proposed on a one-time Companion request nobody has
 * taken, on ONE line — "2 volunteers proposed other times: Wed, Oct 28, 2:00 PM (Sara M.) · Thu, Oct 29, 10:00 AM
 * (Ana R.)" — each with "Use this time". The press is the office's Confirm at a proposed time (the same server
 * function, `what: 'confirm'` with the volunteer's day and time), after a question: agree it with the tenant first.
 * Staff screen only; the tenant is never named to a volunteer here or anywhere.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RequestView } from '@shared/volunteers-api';
import { minutesLabel } from '@shared/volunteers';
import { ApiRequestError } from '@/lib/api';
import { formatDayKeyShort } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { postJson, useVolunteerRefresh } from './api';

type Proposal = NonNullable<RequestView['proposals']>[number];

export function ProposedTimes({ r }: { r: Pick<RequestView, 'requestId' | 'proposals'> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const [asking, setAsking] = useState<Proposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const askRef = useRef<HTMLDivElement | null>(null);
  const list = r.proposals ?? [];
  if (!list.length) return msg ? <p role="status" data-testid={`proposed-msg-${r.requestId}`}>{msg}</p> : null;
  const when = (p: Proposal) => `${formatDayKeyShort(p.dayKey, lang)}, ${minutesLabel(p.minutes)}`;
  const line = t('vol.proposed.line', { count: list.length, times: list.map((p) => t('vol.proposed.time', { when: when(p), name: p.name })).join(' · ') });

  const use = async (p: Proposal) => {
    setBusy(true);
    try {
      await postJson(`/api/volunteers/requests/${r.requestId}`, { what: 'confirm', volunteerId: p.volunteerId, notice: true, wheelchairTraining: false, dayKey: p.dayKey, minutes: p.minutes });
      const m = t('vol.proposed.done', { name: p.name, when: when(p) });
      setMsg(m);
      toast(m);
      announce(m);
      setAsking(null);
      refresh();
    } catch (e) {
      // someone took the original time first: say so in words (the server keeps first-press-wins)
      const m = e instanceof ApiRequestError && e.body?.error === 'claimed' ? t('vol.proposed.taken') : errorText(e);
      setMsg(m);
      announce(m, 'assertive');
      setAsking(null);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="proposed-times" data-testid={`proposed-${r.requestId}`}>
      <p className="help" data-testid={`proposed-line-${r.requestId}`}>
        {line}
      </p>
      <div className="chips">
        {list.map((p) => (
          <Button
            key={p.volunteerId}
            variant="secondary"
            size="sm"
            disabled={busy}
            aria-label={t('vol.proposed.useAria', { when: when(p), name: p.name })}
            onClick={() => {
              setMsg(null);
              setAsking(p);
              window.setTimeout(() => askRef.current?.focus(), 0);
            }}
            data-testid={`proposed-use-${r.requestId}-${p.volunteerId}`}
          >
            {t('vol.proposed.use')}
          </Button>
        ))}
      </div>
      {asking ? (
        <div role="group" aria-label={t('vol.proposed.ask', { when: when(asking), name: asking.name })} ref={askRef} tabIndex={-1} data-testid={`proposed-ask-${r.requestId}`}>
          <p>
            <b>{t('vol.proposed.ask', { when: when(asking), name: asking.name })}</b>
          </p>
          <div className="chips">
            <Button size="sm" disabled={busy} onClick={() => use(asking)} data-testid={`proposed-yes-${r.requestId}`}>
              {t('vol.proposed.yes')}
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setAsking(null)} data-testid={`proposed-back-${r.requestId}`}>
              {t('vol.proposed.back')}
            </Button>
          </div>
        </div>
      ) : null}
      {msg ? (
        <p role="status" data-testid={`proposed-msg-${r.requestId}`}>
          {msg}
        </p>
      ) : null}
    </div>
  );
}
